<script setup>
import { computed } from 'vue';

const props = defineProps({
  series: {
    type: Array,
    default: () => [],
  },
});

// ── SVG geometry ────────────────────────────────────────────────────────────
const W = 760;
const H = 260;
const PAD = { left: 44, right: 16, top: 16, bottom: 28 };
const plotW = W - PAD.left - PAD.right;
const plotH = H - PAD.top - PAD.bottom;
const baseline = PAD.top + plotH;

const maxValue = computed(() => {
  const peak = props.series.reduce((m, p) => Math.max(m, p.sent), 0);
  return peak > 0 ? peak : 1;
});

const hasData = computed(() =>
  props.series.some(p => p.sent > 0 || p.failed > 0)
);

const xAt = index => {
  const n = props.series.length;
  if (n <= 1) return PAD.left + plotW / 2;
  return PAD.left + (index / (n - 1)) * plotW;
};

const yAt = value => baseline - (value / maxValue.value) * plotH;

const points = computed(() =>
  props.series.map((p, i) => ({
    ...p,
    x: xAt(i),
    ySent: yAt(p.sent),
    yDelivered: yAt(p.delivered),
  }))
);

const areaPath = computed(() => {
  if (!points.value.length) return '';
  const tops = points.value.map(p => `L ${p.x},${p.ySent}`).join(' ');
  const first = points.value[0];
  const last = points.value[points.value.length - 1];
  return `M ${first.x},${first.ySent} ${tops} L ${last.x},${baseline} L ${first.x},${baseline} Z`;
});

const sentLine = computed(() =>
  points.value
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.ySent}`)
    .join(' ')
);

const deliveredLine = computed(() =>
  points.value
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.yDelivered}`)
    .join(' ')
);

// ── Axis helpers ────────────────────────────────────────────────────────────
const gridLines = computed(() =>
  [0, 0.25, 0.5, 0.75, 1].map(ratio => ({
    ratio,
    y: baseline - ratio * plotH,
    value: Math.round(maxValue.value * ratio),
  }))
);

const formatDay = date => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

const xLabels = computed(() => {
  const n = props.series.length;
  if (!n) return [];
  const step = Math.max(1, Math.ceil(n / 6));
  const labels = [];
  for (let i = 0; i < n; i += step) {
    labels.push({ x: xAt(i), text: formatDay(props.series[i].date) });
  }
  const last = n - 1;
  if (labels[labels.length - 1]?.text !== formatDay(props.series[last].date)) {
    labels.push({ x: xAt(last), text: formatDay(props.series[last].date) });
  }
  return labels;
});

const fmt = n => new Intl.NumberFormat('pt-BR').format(n ?? 0);
</script>

<template>
  <div class="w-full">
    <div
      v-if="!hasData"
      class="flex items-center justify-center h-40 text-sm text-n-slate-9"
    >
      Sem mensagens enviadas no período selecionado.
    </div>
    <svg
      v-else
      :viewBox="`0 0 ${W} ${H}`"
      class="w-full h-auto"
      role="img"
      aria-label="Enviadas versus entregues por dia"
    >
      <!-- gridlines + y labels -->
      <g class="text-n-slate-4">
        <line
          v-for="g in gridLines"
          :key="`grid-${g.ratio}`"
          :x1="PAD.left"
          :x2="W - PAD.right"
          :y1="g.y"
          :y2="g.y"
          class="stroke-current"
          stroke-width="1"
          :stroke-dasharray="g.ratio === 0 ? '0' : '3 4'"
        />
      </g>
      <g class="text-n-slate-9">
        <text
          v-for="g in gridLines.filter(
            l => l.ratio === 0 || l.ratio === 0.5 || l.ratio === 1
          )"
          :key="`ylabel-${g.ratio}`"
          :x="PAD.left - 8"
          :y="g.y + 4"
          text-anchor="end"
          class="fill-current text-[11px]"
        >
          {{ fmt(g.value) }}
        </text>
      </g>

      <!-- enviadas area + line -->
      <path
        :d="areaPath"
        class="text-n-brand fill-current"
        fill-opacity="0.12"
      />
      <path
        :d="sentLine"
        class="text-n-brand stroke-current"
        fill="none"
        stroke-width="2.5"
        stroke-linejoin="round"
        stroke-linecap="round"
      />
      <!-- entregues line -->
      <path
        :d="deliveredLine"
        class="text-emerald-500 stroke-current"
        fill="none"
        stroke-width="2.5"
        stroke-linejoin="round"
        stroke-linecap="round"
      />

      <!-- hover points -->
      <g v-for="(p, i) in points" :key="`pt-${i}`">
        <circle
          :cx="p.x"
          :cy="p.yDelivered"
          r="2.5"
          class="text-emerald-500 fill-current"
        />
        <circle
          :cx="p.x"
          :cy="p.ySent"
          r="2.5"
          class="text-n-brand fill-current"
        />
        <rect
          :x="p.x - plotW / Math.max(points.length, 1) / 2"
          :y="PAD.top"
          :width="plotW / Math.max(points.length, 1)"
          :height="plotH"
          fill="transparent"
        >
          <title>
            {{ formatDay(p.date) }} — Enviadas: {{ fmt(p.sent) }} · Entregues:
            {{ fmt(p.delivered) }}
          </title>
        </rect>
      </g>

      <!-- x labels -->
      <g class="text-n-slate-9">
        <text
          v-for="(label, i) in xLabels"
          :key="`xlabel-${i}`"
          :x="label.x"
          :y="H - 8"
          text-anchor="middle"
          class="fill-current text-[11px]"
        >
          {{ label.text }}
        </text>
      </g>
    </svg>
  </div>
</template>
